import {
  Col,
  FormInstance,
  InputProps,
  SelectProps,
  FormItemProps,
} from "antd";
import styled from "styled-components";
import { DatePicker, Form, Input, Select } from "@renderbus/ui";
import { ReactNode, useState } from "react";
import { SearchBtn } from "@/components/dynamic-form/components/search-btn";
import { ClearBtn } from "@/components/dynamic-form/components/clear-btn";
import { SearchBarType } from "@/components/dynamic-form/constants";
import { FormProps } from "rc-field-form/lib/Form";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { RangePickerProps } from "antd/es/date-picker/generatePicker";
interface BaseSearchConfig {
  type: SearchBarType;
  label: string;
  hidden?: boolean;
  field: string;
  span?: number;
  formItemProps?: FormItemProps;
  isTrendsChangeDate?: boolean;
  limitMonthNum?: number;
}
export interface IInputSearchConfig extends BaseSearchConfig {
  type: SearchBarType.Input;
  props?: InputProps;
}
export interface ISelectSearchConfig extends BaseSearchConfig {
  type: SearchBarType.Select;
  props?: SelectProps;
}
export interface IDataPickerSearchConfig extends BaseSearchConfig {
  type: SearchBarType.DatePicker;
  props?: RangePickerProps<Dayjs>;
}

export type SearchConfig =
  | IInputSearchConfig
  | ISelectSearchConfig
  | IDataPickerSearchConfig;

export interface SearchBarProps {
  searchConfig: SearchConfig[];
  searchForm: FormInstance;
  searchBtn?: ReactNode;
  clearBtn?: ReactNode;
  buttonList?: ReactNode[];
  showButtonIcon?: boolean;
  onSearch: () => void;
  initialValues?: FormProps["initialValues"];
}
const Components = {
  [SearchBarType.Select]: Select,
  [SearchBarType.Input]: Input,
  [SearchBarType.DatePicker]: DatePicker,
};

export function DynamicForm({
  searchConfig,
  searchForm,
  buttonList,
  onSearch,
  searchBtn,
  initialValues,
  clearBtn,
}: SearchBarProps) {
  const existDatePicker = searchConfig.find(
    (item) => item.type === SearchBarType.DatePicker
  );
  const [currentFormDate, setCurrentFormDate] = useState(
    existDatePicker ? existDatePicker.props?.defaultValue || [] : []
  );
  const [dateValue, setDateValue] = useState(
    existDatePicker ? existDatePicker.props?.defaultValue || [] : []
  );
  const disabledLimitDate = (current: Dayjs) => {
    if (
      !existDatePicker ||
      !existDatePicker.isTrendsChangeDate ||
      !currentFormDate ||
      !existDatePicker.limitMonthNum
    )
      return current > dayjs().endOf("day");
    const tooLate =
      currentFormDate[0] &&
      current >
        dayjs(currentFormDate[0]).add(existDatePicker.limitMonthNum, "month");
    const tooEarly =
      currentFormDate[1] &&
      current <
        dayjs(currentFormDate[1]).subtract(
          existDatePicker.limitMonthNum,
          "month"
        );
    return !!tooEarly || !!tooLate || current > dayjs().endOf("day");
  };
  // 设置月份的开始时间为1号
  const onSetMonthStart = (val: any) => {
    const [start, end] = val;
    const newStart = start.startOf("month"); // 将日期设置为当月第一天
    const newEnd = end.startOf("month");
    return [newStart, newEnd];
  };

  const onOpenChange = (open: boolean) => {
    setCurrentFormDate(open ? [null, null] : null);
    if (existDatePicker) {
      if (open) {
        searchForm.setFieldValue(existDatePicker.field, [null, null]);
      } else {
        const fieldDate =
          (existDatePicker.props as any)?.picker === "month"
            ? onSetMonthStart(dateValue)
            : dateValue;
        searchForm.setFieldValue(existDatePicker.field, fieldDate);
      }
    }
  };

  const onCalendarChange = (val: any) => setCurrentFormDate(val);
  const onChange = (val: any) => setDateValue(val);

  return (
    <SearchWrapper>
      <Form
        form={searchForm}
        initialValues={initialValues}
        layout="inline"
        colon={false}
        size={"middle"}
        style={{ gap: "8px", flexWrap: "nowrap" }}
      >
        <SearchFormContianer>
          {searchConfig.map((item) => {
            const Component = Components[item.type];

            return (
              <Col span={item.span} key={item.field}>
                <Form.Item
                  hidden={item.hidden}
                  key={item.field}
                  name={item.field}
                  label={item.label}
                  {...item.formItemProps}
                >
                  {item.type === SearchBarType.DatePicker ? (
                    <Component
                      disabledDate={disabledLimitDate}
                      onCalendarChange={onCalendarChange}
                      onOpenChange={onOpenChange}
                      onChange={onChange}
                      {...(item.props as any)}
                    />
                  ) : (
                    <Component
                      {...(item.props as any)}
                      onKeyUp={(e: any) => {
                        if (
                          item.type === SearchBarType.Input &&
                          e.key === "Enter"
                        )
                          onSearch();
                      }}
                    />
                  )}
                </Form.Item>
              </Col>
            );
          })}
          {searchBtn ? searchBtn : <SearchBtn onClick={() => onSearch()} />}
          {clearBtn ? (
            clearBtn
          ) : (
            <ClearBtn onClick={() => searchForm.resetFields()} />
          )}

          {buttonList}
        </SearchFormContianer>
      </Form>
    </SearchWrapper>
  );
}
const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  button {
    min-width: 30px;
  }
`;
const SearchFormContianer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
